import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Map, { Marker, Source, Layer, Popup } from "react-map-gl/maplibre";
import type { MapRef } from "react-map-gl/maplibre";
import { api } from "../lib/api";
import PlacePanel from "../components/PlacePanel";
import ShareButton from "../components/ShareButton";
import { OUTREACH_STATUS_LABELS } from "@hsh/shared";
import type { OutreachPlan, Place, OutreachStatus } from "@hsh/shared";
import "maplibre-gl/dist/maplibre-gl.css";

// Custom hook for detecting mobile viewport
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
}

// Default center (County Durham)
const DEFAULT_CENTER = { latitude: 54.7761, longitude: -1.5733, zoom: 12 };

export default function PlanPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plan, setPlan] = useState<OutreachPlan | null>(null);
  const [places, setPlaces] = useState<Place[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredPlace, setHoveredPlace] = useState<Place | null>(null);
  const mapRef = useRef<MapRef>(null);
  const isMobile = useIsMobile();

  // URL-based state
  const activeWeek = parseInt(searchParams.get("week") || "1", 10);
  const viewMode = (searchParams.get("view") as "list" | "map") || "map";
  const selectedPlaceId = searchParams.get("place");

  const setActiveWeek = useCallback(
    (week: number) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (week === 1) {
            newParams.delete("week");
          } else {
            newParams.set("week", String(week));
          }
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setViewMode = useCallback(
    (mode: "list" | "map") => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (mode === "map") {
            newParams.delete("view");
          } else {
            newParams.set("view", mode);
          }
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSelectedPlaceUrl = useCallback(
    (placeId: string | null) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (placeId) {
            newParams.set("place", placeId);
          } else {
            newParams.delete("place");
          }
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleSelectPlace = useCallback(
    (place: Place | null) => {
      setSelectedPlace(place);
      setSelectedPlaceUrl(place?._id || null);
    },
    [setSelectedPlaceUrl]
  );

  useEffect(() => {
    if (id) {
      fetchPlan();
    }
  }, [id]);

  useEffect(() => {
    if (id && plan && activeWeek) {
      fetchWeekPlaces();
    }
  }, [id, plan, activeWeek]);

  // Load selected place from URL
  useEffect(() => {
    if (selectedPlaceId && places.length > 0) {
      const place = places.find((p) => p._id === selectedPlaceId);
      if (place && (!selectedPlace || selectedPlace._id !== selectedPlaceId)) {
        setSelectedPlace(place);
      }
    } else if (!selectedPlaceId && selectedPlace) {
      setSelectedPlace(null);
    }
  }, [selectedPlaceId, places]);

  // Fit map bounds when places change (week switch)
  useEffect(() => {
    if (!mapRef.current || places.length === 0 || viewMode !== "map") return;

    const lngs = places
      .map((p) => p.location?.coordinates?.[0])
      .filter((v): v is number => v !== undefined);
    const lats = places
      .map((p) => p.location?.coordinates?.[1])
      .filter((v): v is number => v !== undefined);

    if (lngs.length === 0 || lats.length === 0) return;

    const bounds: [[number, number], [number, number]] = [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];

    mapRef.current.fitBounds(bounds, {
      padding: 60,
      duration: 500,
    });
  }, [places, viewMode]);

  const fetchPlan = async () => {
    setIsLoading(true);
    try {
      const response = await api.getPlan(id!);
      setPlan(response.data.plan);
      // Don't set all places here - let fetchWeekPlaces handle it
    } catch (error) {
      console.error("Failed to fetch plan:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWeekPlaces = async () => {
    try {
      const response = await api.getWeekPlaces(id!, activeWeek);
      setPlaces(response.data);
    } catch (error) {
      console.error("Failed to fetch week places:", error);
    }
  };

  const handlePlaceUpdate = async () => {
    // First, immediately fetch and update the selected place with fresh data
    // This ensures the activity log shows the new entry right away
    if (selectedPlace) {
      const weekPlacesResponse = await api.getWeekPlaces(id!, activeWeek);
      const updatedPlace = weekPlacesResponse.data.find(
        (p: Place) => p._id === selectedPlace._id
      );
      if (updatedPlace) {
        setSelectedPlace(updatedPlace);
      }
      // Update the places list with fresh data
      setPlaces(weekPlacesResponse.data);
    } else {
      // No selected place, just refresh the week's places
      await fetchWeekPlaces();
    }
    // Also refresh the plan to get updated progress stats
    const response = await api.getPlan(id!);
    setPlan(response.data.plan);
  };

  const getStatusColor = (status: OutreachStatus) => {
    switch (status) {
      case "not_contacted":
        return "bg-gray-100 text-gray-700";
      case "contacted":
        return "bg-blue-100 text-blue-700";
      case "meeting_booked":
        return "bg-green-100 text-green-700";
      case "referred":
        return "bg-purple-100 text-purple-700";
      case "not_suitable":
        return "bg-red-100 text-red-700";
      case "follow_up":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "bg-red-100 text-red-700";
    if (score >= 60) return "bg-orange-100 text-orange-700";
    if (score >= 40) return "bg-yellow-100 text-yellow-700";
    if (score >= 20) return "bg-green-100 text-green-700";
    return "bg-gray-100 text-gray-700";
  };

  // Calculate progress
  const getProgress = () => {
    if (!places.length) return { contacted: 0, total: 0, percentage: 0 };
    const contacted = places.filter(
      (p) => p.outreachStatus !== "not_contacted"
    ).length;
    return {
      contacted,
      total: places.length,
      percentage: Math.round((contacted / places.length) * 100),
    };
  };

  const progress = getProgress();

  // Calculate map bounds and route line
  const mapViewState = useMemo(() => {
    if (places.length === 0) return DEFAULT_CENTER;

    const lats = places
      .map((p) => p.location?.coordinates?.[1] || 0)
      .filter(Boolean);
    const lngs = places
      .map((p) => p.location?.coordinates?.[0] || 0)
      .filter(Boolean);

    if (lats.length === 0 || lngs.length === 0) return DEFAULT_CENTER;

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    return {
      latitude: (minLat + maxLat) / 2,
      longitude: (minLng + maxLng) / 2,
      zoom: 13,
    };
  }, [places]);

  // Create GeoJSON for route line
  const routeGeoJson = useMemo(() => {
    const coordinates = places
      .map((p) => p.location?.coordinates)
      .filter((c): c is [number, number] => c !== undefined);

    return {
      type: "Feature" as const,
      properties: {},
      geometry: {
        type: "LineString" as const,
        coordinates,
      },
    };
  }, [places]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-500">Loading plan...</div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Plan not found
          </h2>
          <Link to="/map" className="text-primary-600 hover:text-primary-700">
            Return to map
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col md:flex-row overflow-hidden">
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-6">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 mb-4 md:mb-6">
          <Link
            to="/plans"
            className="text-sm text-primary-600 hover:text-primary-700 mb-2 inline-block"
          >
            ← Back to Plans
          </Link>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            {plan.name}
          </h1>
          {plan.description && (
            <p className="text-gray-600 mt-1 text-sm md:text-base">
              {plan.description}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-2 md:mt-3 text-xs md:text-sm text-gray-500">
            <span>
              {new Date(plan.startDate).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
              })}{" "}
              -{" "}
              {new Date(plan.endDate).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
            <span className="hidden md:inline">•</span>
            <span>{Math.ceil((plan.durationDays || 56) / 7)} weeks</span>
            <span>•</span>
            <span>{plan.targetCount} targets</span>
            <ShareButton className="ml-auto" />
          </div>
        </div>

        {/* Week Progress Bar - Fixed */}
        <div className="flex-shrink-0 bg-white rounded-lg shadow-sm p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-gray-900 text-sm md:text-base">
              Week {activeWeek} Progress
            </h3>
            <span className="text-xs md:text-sm text-gray-600">
              {progress.contacted} / {progress.total} contacted (
              {progress.percentage}%)
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 md:h-3">
            <div
              className="bg-primary-600 h-2 md:h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>

        {/* Week Selector + View Toggle - Fixed */}
        <div className="flex-shrink-0 mb-4 md:mb-6">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <h3 className="font-medium text-gray-900 text-sm md:text-base">
              Weekly Call List
            </h3>
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  viewMode === "list"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 10h16M4 14h16M4 18h16"
                  />
                </svg>
                List
              </button>
              <button
                onClick={() => setViewMode("map")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  viewMode === "map"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                  />
                </svg>
                Map
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 md:gap-2">
            {plan.weeklyTargets.map((week) => (
              <button
                key={week.weekNumber}
                onClick={() => setActiveWeek(week.weekNumber)}
                className={`px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                  activeWeek === week.weekNumber
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                W{week.weekNumber}
                <span className="ml-1 text-xs opacity-75">
                  ({week.targetCount})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Map View */}
        {viewMode === "map" && (
          <div className="flex-1 min-h-0 bg-white rounded-lg shadow-sm overflow-hidden">
            <Map
              ref={mapRef}
              initialViewState={mapViewState}
              style={{ width: "100%", height: "100%" }}
              mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
            >
              {/* Route line */}
              {places.length > 1 && (
                <Source id="route" type="geojson" data={routeGeoJson}>
                  <Layer
                    id="route-line"
                    type="line"
                    paint={{
                      "line-color": "#6366f1",
                      "line-width": 3,
                      "line-opacity": 0.7,
                      "line-dasharray": [2, 1],
                    }}
                  />
                </Source>
              )}

              {/* Numbered markers */}
              {places.map((place, index) => {
                const coords = place.location?.coordinates;
                if (!coords) return null;

                const isCompleted = place.outreachStatus !== "not_contacted";

                return (
                  <Marker
                    key={place._id}
                    longitude={coords[0]}
                    latitude={coords[1]}
                    anchor="center"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      handleSelectPlace(place);
                    }}
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer shadow-lg border-2 border-white transition-transform hover:scale-110 ${
                        isCompleted
                          ? "bg-green-500 text-white"
                          : "bg-primary-600 text-white"
                      }`}
                      onMouseEnter={() => setHoveredPlace(place)}
                      onMouseLeave={() => setHoveredPlace(null)}
                    >
                      {isCompleted ? "✓" : index + 1}
                    </div>
                  </Marker>
                );
              })}

              {/* Hover popup */}
              {hoveredPlace && hoveredPlace.location?.coordinates && (
                <Popup
                  longitude={hoveredPlace.location.coordinates[0]}
                  latitude={hoveredPlace.location.coordinates[1]}
                  anchor="bottom"
                  closeButton={false}
                  closeOnClick={false}
                  offset={15}
                >
                  <div className="p-1">
                    <p className="font-medium text-sm">{hoveredPlace.name}</p>
                    <p className="text-xs text-gray-500">
                      {hoveredPlace.address}
                    </p>
                  </div>
                </Popup>
              )}
            </Map>
          </div>
        )}

        {/* Places List - Scrollable */}
        {viewMode === "list" && (
          <div className="flex-1 min-h-0 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
            <div className="flex-shrink-0 p-3 md:p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900 text-sm md:text-base">
                    Visit Route – Week {activeWeek}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {places.length} businesses in optimized walking order
                  </p>
                </div>
                {(() => {
                  const activeWeekData = plan.weeklyTargets.find(
                    (w) => w.weekNumber === activeWeek
                  );
                  if (activeWeekData) {
                    return (
                      <div className="text-right">
                        <p className="text-xs md:text-sm text-gray-600">
                          {new Date(
                            activeWeekData.startDate
                          ).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                          })}{" "}
                          –{" "}
                          {new Date(activeWeekData.endDate).toLocaleDateString(
                            "en-GB",
                            { day: "numeric", month: "short" }
                          )}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {places.map((place, index) => (
                <div
                  key={place._id}
                  onClick={() => handleSelectPlace(place)}
                  className={`p-3 md:p-4 hover:bg-gray-50 cursor-pointer flex items-start gap-3 active:bg-gray-100 border-b border-gray-100 ${
                    place.outreachStatus !== "not_contacted"
                      ? "bg-gray-50/50"
                      : ""
                  }`}
                >
                  {/* Visit Number */}
                  <div
                    className={`flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      place.outreachStatus !== "not_contacted"
                        ? "bg-green-100 text-green-700"
                        : "bg-primary-100 text-primary-700"
                    }`}
                  >
                    {place.outreachStatus !== "not_contacted" ? "✓" : index + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1 md:gap-2 mb-0.5">
                      <h4 className="font-medium text-gray-900 text-sm md:text-base truncate">
                        {place.name}
                      </h4>
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs ${getScoreColor(
                          place.score
                        )}`}
                      >
                        {place.score}
                      </span>
                    </div>
                    <p className="text-xs md:text-sm text-gray-600 truncate">
                      {place.address}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                        {place.category}
                      </span>
                      {place.town && (
                        <span className="text-xs text-gray-400">
                          {place.town}
                        </span>
                      )}
                      {place.phone && (
                        <a
                          href={`tel:${place.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-primary-600 hover:underline hidden md:inline"
                        >
                          {place.phone}
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={`px-1.5 md:px-2 py-0.5 md:py-1 rounded text-xs whitespace-nowrap ${getStatusColor(
                        place.outreachStatus
                      )}`}
                    >
                      {OUTREACH_STATUS_LABELS[place.outreachStatus]}
                    </span>
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              ))}
              {places.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  No businesses for this week
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Place Detail Panel - Desktop */}
      {selectedPlace && !isMobile && (
        <div className="w-96 bg-white border-l border-gray-200 overflow-hidden flex flex-col flex-shrink-0">
          <PlacePanel
            place={selectedPlace}
            onClose={() => handleSelectPlace(null)}
            planId={id}
            onUpdate={handlePlaceUpdate}
          />
        </div>
      )}

      {/* Place Detail Panel - Mobile (Full Screen) */}
      {selectedPlace && isMobile && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <PlacePanel
            place={selectedPlace}
            onClose={() => handleSelectPlace(null)}
            planId={id}
            onUpdate={handlePlaceUpdate}
            isMobile
          />
        </div>
      )}
    </div>
  );
}
